import { FormattedMessage, defineMessage } from 'react-intl';
import _defineProperty from '@babel/runtime/helpers/defineProperty';
import _objectSpread from '@babel/runtime/helpers/objectSpread2';
import _toConsumableArray from '@babel/runtime/helpers/toConsumableArray';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import classNames from 'classnames';
import isPlainObject from 'lodash/isPlainObject';
import PropTypes from 'prop-types';
import React, { useState, useCallback, useRef, useEffect } from 'react';
import { PropTypes as PropTypes$1 } from '@micromag/core';
import { ScreenElement } from '@micromag/core/components';
import { useScreenSize, useViewerContext, useViewerWebView, usePlaybackContext, usePlaybackMediaRef, useScreenRenderContext } from '@micromag/core/contexts';
import { useDimensionObserver } from '@micromag/core/hooks';
import { isHeaderFilled, isFooterFilled, getFooterProps, isImageFilled, isTextFilled } from '@micromag/core/utils';
import Background from '@micromag/element-background';
import Container from '@micromag/element-container';
import Footer from '@micromag/element-footer';
import Grid from '@micromag/element-grid';
import Header from '@micromag/element-header';
import Text from '@micromag/element-text';
import Visual from '@micromag/element-visual';
import _objectDestructuringEmpty from '@babel/runtime/helpers/objectDestructuringEmpty';
import _extends from '@babel/runtime/helpers/extends';

var layoutProps = {
  // 2
  'two-vertical-equal': {
    vertical: true,
    layout: [{
      columns: 1,
      rows: [1, 1]
    }]
  },
  'two-vertical-top': {
    vertical: true,
    layout: [{
      columns: 1,
      rows: [2, 1]
    }]
  },
  'two-vertical-bottom': {
    vertical: true,
    layout: [{
      columns: 1,
      rows: [1, 2]
    }]
  },
  // 3
  'three-vertical': {
    vertical: true,
    layout: [{
      columns: 1,
      rows: [1, 1, 1]
    }]
  },
  'one-two': {
    layout: [{
      rows: 2,
      columns: [1]
    }, {
      rows: 1,
      columns: [1, 1]
    }]
  },
  'two-one': {
    layout: [{
      rows: 1,
      columns: [1, 1]
    }, {
      rows: 2,
      columns: [1]
    }]
  },
  // 4
  'two-by-two': {
    layout: [{
      rows: 1,
      columns: [1, 1]
    }, {
      rows: 1,
      columns: [1, 1]
    }]
  },
  'four-vertical': {
    vertical: true,
    layout: [{
      columns: 1,
      rows: [1, 1, 1, 1]
    }]
  },
  'one-two-one': {
    layout: [{
      rows: 3,
      columns: [1]
    }, {
      rows: 2,
      columns: [1, 1]
    }, {
      rows: 3,
      columns: [1]
    }]
  },
  'four-mosaic': {
    vertical: true,
    layout: [{
      columns: 1,
      rows: [2, 1]
    }, {
      columns: 1,
      rows: [1, 2]
    }]
  },
  // 5
  'two-one-two': {
    layout: [{
      rows: 2,
      columns: [1, 1]
    }, {
      rows: 3,
      columns: [1]
    }, {
      rows: 2,
      columns: [1, 1]
    }]
  },
  'one-two-two': {
    layout: [{
      rows: 3,
      columns: [1]
    }, {
      rows: 2,
      columns: [1, 1]
    }, {
      rows: 2,
      columns: [1, 1]
    }]
  },
  'two-two-one': {
    layout: [{
      rows: 2,
      columns: [1, 1]
    }, {
      rows: 2,
      columns: [1, 1]
    }, {
      rows: 3,
      columns: [1]
    }]
  },
  // 6
  'two-by-three': {
    layout: [{
      rows: 1,
      columns: [1, 1]
    }, {
      rows: 1,
      columns: [1, 1]
    }, {
      rows: 1,
      columns: [1, 1]
    }]
  },
  'one-one-two-two': {
    layout: [{
      rows: 1,
      columns: [1]
    }, {
      rows: 1,
      columns: [1]
    }, {
      rows: 1,
      columns: [1, 1]
    }, {
      rows: 1,
      columns: [1, 1]
    }]
  },
  'two-two-one-one': {
    layout: [{
      rows: 1,
      columns: [1, 1]
    }, {
      rows: 1,
      columns: [1, 1]
    }, {
      rows: 1,
      columns: [1]
    }, {
      rows: 1,
      columns: [1]
    }]
  }
};

var styles = {"container":"micromag-screen-gallery-container","image":"micromag-screen-gallery-image","inner":"micromag-screen-gallery-inner","background":"micromag-screen-gallery-background","placeholder":"micromag-screen-gallery-placeholder","content":"micromag-screen-gallery-content","emptyImage":"micromag-screen-gallery-emptyImage","emptyCaption":"micromag-screen-gallery-emptyCaption","grid":"micromag-screen-gallery-grid","gridItem":"micromag-screen-gallery-gridItem","imageContainer":"micromag-screen-gallery-imageContainer","header":"micromag-screen-gallery-header","footer":"micromag-screen-gallery-footer","caption":"micromag-screen-gallery-caption"};

var propTypes = {
  layout: PropTypes.oneOf([
  // 2
  'two-vertical-equal', 'two-vertical-top', 'two-vertical-bottom',
  // 3
  'three-vertical', 'one-two', 'two-one',
  // 4
  'two-by-two', 'four-vertical', 'one-two-one', 'four-mosaic',
  // 5
  'two-one-two', 'one-two-two', 'two-two-one',
  // 6
  'two-by-three', 'one-one-two-two', 'two-two-one-one']),
  images: PropTypes.oneOfType([PropTypes$1.imageMedias, PropTypes$1.imageElements]),
  withCaptions: PropTypes.bool,
  imageCaptionStyle: PropTypes$1.textStyle,
  spacing: PropTypes.number,
  captionMaxLines: PropTypes.number,
  background: PropTypes$1.backgroundElement,
  header: PropTypes$1.header,
  footer: PropTypes$1.footer,
  current: PropTypes.bool,
  active: PropTypes.bool,
  preload: PropTypes.bool,
  className: PropTypes.string
};
var defaultProps = {
  layout: 'four-mosaic',
  withCaptions: false,
  imageCaptionStyle: null,
  images: [],
  spacing: 20,
  captionMaxLines: 2,
  background: null,
  header: null,
  footer: null,
  current: true,
  active: true,
  preload: true,
  className: null
};
var GalleryScreen = function GalleryScreen(_ref) {
  var layout = _ref.layout,
    images = _ref.images,
    withCaptions = _ref.withCaptions,
    imageCaptionStyle = _ref.imageCaptionStyle,
    background = _ref.background,
    header = _ref.header,
    footer = _ref.footer,
    current = _ref.current,
    active = _ref.active,
    preload = _ref.preload,
    initialSpacing = _ref.spacing,
    captionMaxLines = _ref.captionMaxLines,
    className = _ref.className;
  var _useScreenSize = useScreenSize(),
    width = _useScreenSize.width,
    height = _useScreenSize.height,
    resolution = _useScreenSize.resolution;
  var _useViewerContext = useViewerContext(),
    viewerTopHeight = _useViewerContext.topHeight,
    viewerBottomHeight = _useViewerContext.bottomHeight,
    viewerBottomSidesWidth = _useViewerContext.bottomSidesWidth;
  var _useViewerWebView = useViewerWebView(),
    openWebView = _useViewerWebView.open;
  var _usePlaybackContext = usePlaybackContext(),
    muted = _usePlaybackContext.muted;
  var mediaRef = usePlaybackMediaRef(current);
  var spacing = initialSpacing !== null ? Math.max(0, initialSpacing || 0) : 20;
  var _useScreenRenderConte = useScreenRenderContext(),
    isView = _useScreenRenderConte.isView,
    isPreview = _useScreenRenderConte.isPreview,
    isPlaceholder = _useScreenRenderConte.isPlaceholder,
    isEdit = _useScreenRenderConte.isEdit;
  var backgroundPlaying = current && (isView || isEdit);
  var mediaShouldLoad = current || preload;
  var finalSpacing = isPlaceholder ? 5 : spacing;
  var grid = isPlainObject(layoutProps[layout]) ? layoutProps[layout] : {};
  var _grid$layout = grid.layout,
    gridLayout = _grid$layout === void 0 ? [] : _grid$layout,
    _grid$vertical = grid.vertical,
    vertical = _grid$vertical === void 0 ? false : _grid$vertical;
  var gridSpaces = gridLayout.reduce(function (acc, _ref2) {
    var rows = _ref2.rows,
      columns = _ref2.columns;
    return acc + (vertical ? rows : columns).length;
  }, 0);
  var _useState = useState(0),
    _useState2 = _slicedToArray(_useState, 2),
    imagesLoaded = _useState2[0],
    setImagesLoaded = _useState2[1];
  var imagesCount = images !== null ? Math.min(gridSpaces, images.length) : 0;
  var ready = imagesLoaded >= imagesCount;
  var onImageLoaded = useCallback(function () {
    setImagesLoaded(function (count) {
      return count + 1;
    });
  }, [setImagesLoaded]);
  var imagesEl = useRef([]);
  var _useState3 = useState([]),
    _useState4 = _slicedToArray(_useState3, 2),
    imagesSizes = _useState4[0],
    setImagesSizes = _useState4[1];
  var _useDimensionObserver = useDimensionObserver(),
    contentRef = _useDimensionObserver.ref,
    _useDimensionObserver2 = _useDimensionObserver.width,
    contentWidth = _useDimensionObserver2 === void 0 ? null : _useDimensionObserver2,
    _useDimensionObserver3 = _useDimensionObserver.height,
    contentHeight = _useDimensionObserver3 === void 0 ? null : _useDimensionObserver3;
  useEffect(function () {
    if (imagesEl.current.length) {
      setImagesSizes(imagesEl.current.map(function (imageEl) {
        return imageEl !== null ? {
          width: imageEl.offsetWidth,
          height: imageEl.offsetHeight
        } : {};
      }));
    }
  }, [contentWidth, contentHeight, layout, setImagesSizes]);

  // header + footer
  var hasHeader = isHeaderFilled(header);
  var hasFooter = isFooterFilled(footer);
  var footerProps = getFooterProps(footer, {
    isView: isView,
    current: current,
    openWebView: openWebView,
    isPreview: isPreview
  });
  var _useDimensionObserver4 = useDimensionObserver(),
    headerRef = _useDimensionObserver4.ref,
    _useDimensionObserver5 = _useDimensionObserver4.height,
    headerHeight = _useDimensionObserver5 === void 0 ? 0 : _useDimensionObserver5;
  var _useDimensionObserver6 = useDimensionObserver(),
    footerRef = _useDimensionObserver6.ref,
    _useDimensionObserver7 = _useDimensionObserver6.height,
    footerHeight = _useDimensionObserver7 === void 0 ? 0 : _useDimensionObserver7;

  // items
  var items = _toConsumableArray(Array(gridSpaces)).map(function (item, itemI) {
    var image = images !== null ? images[itemI] : null;
    var imageSize = imagesSizes[itemI] || {};
    var finalImage = withCaptions ? image : {
      media: image
    };
    var _ref3 = finalImage || {},
      _ref3$caption = _ref3.caption,
      caption = _ref3$caption === void 0 ? null : _ref3$caption;
    var hasImage = isImageFilled(finalImage);
    var hasCaption = isTextFilled(caption);
    var _ref4 = caption || {},
      _ref4$textStyle = _ref4.textStyle,
      textStyle = _ref4$textStyle === void 0 ? null : _ref4$textStyle;
    return /*#__PURE__*/React.createElement("div", {
      key: "item-".concat(itemI),
      className: styles.gridItem
    }, /*#__PURE__*/React.createElement("div", {
      className: styles.imageContainer,
      ref: function ref(el) {
        imagesEl.current[itemI] = el;
      }
    }, /*#__PURE__*/React.createElement(ScreenElement, {
      placeholder: "image",
      placeholderProps: {
        className: styles.placeholder,
        height: '100%'
      },
      emptyLabel: /*#__PURE__*/React.createElement(FormattedMessage, {
        id: "ebsPd4",
        defaultMessage: [{
          "type": 0,
          "value": "Image"
        }]
      }),
      emptyClassName: styles.emptyImage,
      isEmpty: !hasImage
    }, active || current ? /*#__PURE__*/React.createElement(Visual, Object.assign({
      className: styles.image
    }, finalImage, imageSize, {
      resolution: resolution,
      objectFit: {
        fit: 'cover'
      },
      playing: backgroundPlaying,
      active: active,
      shouldLoad: mediaShouldLoad,
      withoutVideo: isPreview,
      onLoaded: onImageLoaded
    })) : null)), withCaptions ? /*#__PURE__*/React.createElement(ScreenElement, {
      placeholder: "line",
      emptyLabel: /*#__PURE__*/React.createElement(FormattedMessage, {
        id: "6tIigB",
        defaultMessage: [{
          "type": 0,
          "value": "Caption"
        }]
      }),
      emptyClassName: styles.emptyCaption,
      isEmpty: !hasCaption
    }, /*#__PURE__*/React.createElement("div", {
      className: styles.caption
    }, /*#__PURE__*/React.createElement(Text, Object.assign({}, caption, {
      className: styles.captionText,
      lineClamp: captionMaxLines,
      textStyle: _objectSpread(_objectSpread({}, imageCaptionStyle || null), textStyle)
    })))) : null);
  });
  return /*#__PURE__*/React.createElement("div", {
    className: classNames([styles.container, _defineProperty(_defineProperty({}, className, className !== null), styles.isPlaceholder, isPlaceholder)]),
    "data-screen-ready": ready
  }, /*#__PURE__*/React.createElement(Container, {
    width: width,
    height: height,
    className: styles.content
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.inner,
    style: {
      paddingTop: (hasHeader ? headerHeight : 0) + (!isPreview ? viewerTopHeight : 0),
      paddingBottom: (hasFooter ? footerHeight : 0) + (current && !isPreview ? viewerBottomHeight : 0)
    },
    ref: contentRef
  }, !isPlaceholder && hasHeader ? /*#__PURE__*/React.createElement("div", {
    className: styles.header,
    ref: headerRef,
    style: {
      paddingTop: hasHeader ? finalSpacing / 2 : spacing,
      paddingLeft: spacing,
      paddingRight: spacing,
      transform: !isPreview ? "translate(0, ".concat(viewerTopHeight, "px)") : null
    }
  }, /*#__PURE__*/React.createElement(Header, header)) : null, /*#__PURE__*/React.createElement(Grid, Object.assign({
    className: styles.grid,
    spacing: finalSpacing,
    items: items
  }, grid)), !isPlaceholder && hasFooter ? /*#__PURE__*/React.createElement("div", {
    className: styles.footer,
    ref: footerRef,
    style: {
      paddingLeft: Math.max(finalSpacing / 2, viewerBottomSidesWidth),
      paddingRight: Math.max(finalSpacing / 2, viewerBottomSidesWidth),
      // paddingTop: finalSpacing / 2,
      paddingBottom: finalSpacing / 2,
      transform: !isPreview ? "translate(0, -".concat(viewerBottomHeight, "px)") : null
    }
  }, /*#__PURE__*/React.createElement(Footer, footerProps)) : null)), !isPlaceholder ? /*#__PURE__*/React.createElement(Background, {
    background: background,
    width: width,
    height: height,
    resolution: resolution,
    playing: backgroundPlaying,
    muted: muted,
    shouldLoad: mediaShouldLoad,
    mediaRef: mediaRef,
    withoutVideo: isPreview,
    className: styles.background
  }) : null);
};
GalleryScreen.propTypes = propTypes;
GalleryScreen.defaultProps = defaultProps;

function GalleryCaptionsScreen(_ref) {
  var props = _extends({}, (_objectDestructuringEmpty(_ref), _ref));
  return /*#__PURE__*/React.createElement(GalleryScreen, Object.assign({}, props, {
    withCaptions: true
  }));
}

// import * as transforms from './transforms/index';

var definition = [{
  id: 'gallery',
  type: 'screen',
  group: {
    label: defineMessage({
      id: "6mX2ya",
      defaultMessage: [{
        "type": 0,
        "value": "Images"
      }]
    }),
    order: 4
  },
  title: defineMessage({
    id: "jaFXki",
    defaultMessage: [{
      "type": 0,
      "value": "Gallery"
    }]
  }),
  component: GalleryScreen,
  layouts: [
  // 2
  'two-vertical-equal', 'two-vertical-top', 'two-vertical-bottom',
  // 3
  'three-vertical', 'one-two', 'two-one',
  // 4
  'two-by-two', 'four-vertical', 'one-two-one', 'four-mosaic',
  // 5
  'two-one-two', 'one-two-two', 'two-two-one',
  // 6
  'two-by-three', 'one-one-two-two', 'two-two-one-one'],
  // transforms,
  fields: [{
    name: 'layout',
    type: 'screen-layout',
    defaultValue: 'two-vertical-equal',
    label: defineMessage({
      id: "4iBXj2",
      defaultMessage: [{
        "type": 0,
        "value": "Layout"
      }]
    })
  }, {
    name: 'images',
    type: 'visuals',
    label: defineMessage({
      id: "muYwrB",
      defaultMessage: [{
        "type": 0,
        "value": "Images"
      }]
    })
  }, {
    name: 'background',
    type: 'background',
    label: defineMessage({
      id: "+MPZRu",
      defaultMessage: [{
        "type": 0,
        "value": "Background"
      }]
    })
  }, {
    name: 'header',
    type: 'header',
    label: defineMessage({
      id: "rhuDxI",
      defaultMessage: [{
        "type": 0,
        "value": "Header"
      }]
    }),
    theme: {
      badge: {
        label: {
          textStyle: 'badge'
        },
        boxStyle: 'badge'
      }
    }
  }, {
    name: 'footer',
    type: 'footer',
    label: defineMessage({
      id: "g4nybp",
      defaultMessage: [{
        "type": 0,
        "value": "Footer"
      }]
    }),
    theme: {
      callToAction: {
        label: {
          textStyle: 'cta'
        },
        boxStyle: 'cta'
      }
    }
  }]
}, {
  id: 'gallery-captions',
  type: 'screen',
  group: {
    label: defineMessage({
      id: "6mX2ya",
      defaultMessage: [{
        "type": 0,
        "value": "Images"
      }]
    }),
    order: 4
  },
  title: defineMessage({
    id: "MmJQ7o",
    defaultMessage: [{
      "type": 0,
      "value": "Gallery with captions"
    }]
  }),
  component: GalleryCaptionsScreen,
  layouts: [
  // 2
  'two-vertical-equal', 'two-vertical-top', 'two-vertical-bottom',
  // 3
  'three-vertical', 'one-two', 'two-one',
  // 4
  'two-by-two', 'four-vertical', 'one-two-one', 'four-mosaic',
  // 5
  'two-one-two', 'one-two-two', 'two-two-one',
  // 6
  'two-by-three', 'one-one-two-two', 'two-two-one-one'],
  // transforms,
  fields: [{
    name: 'layout',
    type: 'screen-layout',
    defaultValue: 'two-vertical-equal',
    label: defineMessage({
      id: "4iBXj2",
      defaultMessage: [{
        "type": 0,
        "value": "Layout"
      }]
    })
  }, {
    name: 'images',
    type: 'visuals-with-caption',
    theme: {
      caption: {
        textStyle: 'text'
      }
    },
    label: defineMessage({
      id: "59f2bg",
      defaultMessage: [{
        "type": 0,
        "value": "Images"
      }]
    })
  }, {
    name: 'imageCaptionStyle',
    type: 'text-style-form',
    label: defineMessage({
      id: "Qz7nUz",
      defaultMessage: [{
        "type": 0,
        "value": "Caption style"
      }]
    })
  }, {
    name: 'background',
    type: 'background',
    label: defineMessage({
      id: "+MPZRu",
      defaultMessage: [{
        "type": 0,
        "value": "Background"
      }]
    })
  }, {
    name: 'header',
    type: 'header',
    label: defineMessage({
      id: "rhuDxI",
      defaultMessage: [{
        "type": 0,
        "value": "Header"
      }]
    }),
    theme: {
      badge: {
        label: {
          textStyle: 'badge'
        },
        boxStyle: 'badge'
      }
    }
  }, {
    name: 'footer',
    type: 'footer',
    label: defineMessage({
      id: "g4nybp",
      defaultMessage: [{
        "type": 0,
        "value": "Footer"
      }]
    }),
    theme: {
      callToAction: {
        label: {
          textStyle: 'cta'
        },
        boxStyle: 'cta'
      }
    }
  }]
}];

export { GalleryCaptionsScreen, GalleryScreen, definition as default };
